/* eslint-disable react/jsx-no-bind */
import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { SelectChangeEvent } from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import {
  IoSearch,
  IoClose,
  IoCheckmarkCircleOutline,
  IoTimeOutline,
  IoAlertCircleOutline,
  IoDocumentTextOutline,
  IoImageOutline,
  IoCloseCircleOutline,
} from "react-icons/io5";
import DatePickerRangeG from "components/DatePickerRangeG";
import SelectG from "components/SelectG";
import {
  WhatsAppHistoryEntry,
  WhatsAppHistoryFilters,
  WhatsAppHistoryStatusCode,
  WhatsAppHistoryRecipient,
} from "services/api/AppWeb_WhatsApp/types";
import * as S from "../styles";

interface HistoricoEnviosPanelProps {
  rows: WhatsAppHistoryEntry[];
  loading?: boolean;
  filters: WhatsAppHistoryFilters;
  onFiltersChange: (filters: WhatsAppHistoryFilters) => void;
}

type Situacao = "AGENDADO" | "ENVIADO" | "PENDENTE" | "ERRO";
type LogTone = "enviado" | "pendente" | "erro";

// Opções do dropdown padrão (SelectG) → código numérico do backend.
const STATUS_OPTIONS = [
  { label: "Todos", value: "" },
  { label: "Pendente", value: "1" },
  { label: "Enviado", value: "2" },
  { label: "Erro", value: "3" },
];

function formatDataHora(texto: string): string {
  const value = String(texto || "").trim();
  if (!value) return "-";
  return value;
}

function formatQuantidade(valor?: number): string {
  if (typeof valor !== "number" || Number.isNaN(valor)) return "0";
  return String(valor);
}

// Descrição de cada contador (Enviado / Pendente / Erro) para o tooltip,
// no singular, no plural e quando não há nenhum registro.
const COUNT_PILL_DESCRIPTION: Record<
  LogTone,
  { zero: string; um: string; muitos: string }
> = {
  enviado: {
    zero: "Nenhum contato recebeu a mensagem",
    um: "contato recebeu a mensagem",
    muitos: "contatos receberam a mensagem",
  },
  pendente: {
    zero: "Nenhum contato aguardando envio",
    um: "contato aguardando envio",
    muitos: "contatos aguardando envio",
  },
  erro: {
    zero: "Nenhuma falha no envio",
    um: "contato com falha no envio",
    muitos: "contatos com falha no envio",
  },
};

// Monta o texto do tooltip dos contadores; quando há registros, avisa que a
// pílula é clicável e abre o log de contatos.
function countPillTooltip(tone: LogTone, quantidade: number): string {
  const descricao = COUNT_PILL_DESCRIPTION[tone];
  if (quantidade <= 0) return descricao.zero;
  const texto = quantidade === 1 ? descricao.um : descricao.muitos;
  return `${quantidade} ${texto}. Clique para ver o log de contatos.`;
}

const SITUACAO_LABEL: Record<Situacao, string> = {
  AGENDADO: "Agendado",
  ENVIADO: "Enviado",
  PENDENTE: "Pendente",
  ERRO: "Erro",
};

// Explicação de cada situação, exibida no tooltip da coluna Status.
const SITUACAO_TOOLTIP: Record<Situacao, string> = {
  AGENDADO: "Agendado: as mensagens serão disparadas na data/hora programada.",
  ENVIADO: "Enviado: todas as mensagens foram enviadas com sucesso.",
  PENDENTE: "Pendente: ainda há mensagens aguardando envio.",
  ERRO: "Erro: uma ou mais mensagens não foram entregues.",
};

// Deriva a situação do envio a partir das contagens (e da data de
// agendamento, quando o backend a envia para o futuro). Os rótulos seguem
// as colunas Enviado/Pendente/Erro.
function resolveSituacao(item: WhatsAppHistoryEntry): Situacao {
  const agendadoSort = item.dataEnvioSort;
  if (agendadoSort) {
    const agendado = new Date(agendadoSort).getTime();
    if (!Number.isNaN(agendado) && agendado > Date.now()) {
      return "AGENDADO";
    }
  }
  const erro = item.qtdErro ?? 0;
  if (erro > 0) return "ERRO";
  const pendente = item.qtdPendente ?? 0;
  if (pendente > 0) return "PENDENTE";
  return "ENVIADO";
}

// Converte um Date (do DatePickerRangeG) para ISO completo.
function dateToIso(date: Date | null, endOfDay = false): string | null {
  if (!date || Number.isNaN(date.getTime())) return null;
  const d = new Date(date);
  if (endOfDay) {
    d.setHours(23, 59, 59, 999);
  } else {
    d.setHours(0, 0, 0, 0);
  }
  return d.toISOString();
}

// Converte ISO de volta para Date (estado dos campos).
function isoToDate(iso?: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

const HistoricoEnviosPanel: React.FC<HistoricoEnviosPanelProps> = ({
  rows,
  loading = false,
  filters,
  onFiltersChange,
}) => {
  const [detail, setDetail] = useState<WhatsAppHistoryEntry | null>(null);
  const [logModal, setLogModal] = useState<{
    entry: WhatsAppHistoryEntry;
    tone: LogTone;
  } | null>(null);

  // Estado local dos campos de filtro (aplicados ao alterar).
  const [dataInicial, setDataInicial] = useState<Date | null>(
    isoToDate(filters.dataInicial)
  );
  const [dataFinal, setDataFinal] = useState<Date | null>(
    isoToDate(filters.dataFinal)
  );
  const [statusSelect, setStatusSelect] = useState<string | number>(
    filters.status ? String(filters.status) : ""
  );

  function aplicarFiltros(
    proximaDataInicial: Date | null,
    proximaDataFinal: Date | null,
    proximoStatus: string | number
  ) {
    onFiltersChange({
      status: proximoStatus
        ? (Number(proximoStatus) as WhatsAppHistoryStatusCode)
        : null,
      dataInicial: dateToIso(proximaDataInicial, false),
      dataFinal: dateToIso(proximaDataFinal, true),
    });
  }

  function handleDataInicial(value: Date | null) {
    setDataInicial(value);
    aplicarFiltros(value, dataFinal, statusSelect);
  }

  function handleDataFinal(value: Date | null) {
    setDataFinal(value);
    aplicarFiltros(dataInicial, value, statusSelect);
  }

  function handleStatus(value: string | number) {
    setStatusSelect(value);
    aplicarFiltros(dataInicial, dataFinal, value);
  }

  function limparFiltros() {
    setDataInicial(null);
    setDataFinal(null);
    setStatusSelect("");
    onFiltersChange({});
  }

  const temFiltroAtivo = !!dataInicial || !!dataFinal || statusSelect !== "";

  return (
    <S.HistoryPanel>
      <S.HistoryHeader>
        <S.HistoryTitle>Histórico de Envios em Massa</S.HistoryTitle>
        <S.HistoryHeaderFilters>
          <DatePickerRangeG
            startDate={dataInicial}
            endDate={dataFinal}
            onStartDateChange={handleDataInicial}
            onEndDateChange={handleDataFinal}
            disabled={loading}
            width="auto"
          />
          <S.HistoryHeaderFilterField>
            <label>Status:</label>
            <SelectG
              label=""
              data={STATUS_OPTIONS}
              value={statusSelect}
              placeholder="Todos"
              width="130px"
              disabled={loading}
              onChange={(e: SelectChangeEvent<string | number>) =>
                handleStatus(e.target.value)
              }
            />
          </S.HistoryHeaderFilterField>
          <S.HistoryClearFiltersButton
            type="button"
            onClick={limparFiltros}
            disabled={loading || !temFiltroAtivo}
          >
            <IoCloseCircleOutline size={14} />
            Limpar filtros
          </S.HistoryClearFiltersButton>
        </S.HistoryHeaderFilters>
      </S.HistoryHeader>

      <S.HistoryTableWrap>
        <S.HistoryTable>
          <thead>
            <tr>
              <S.HistoryHeadCell>Mensagem</S.HistoryHeadCell>
              <S.HistoryHeadCell>Status</S.HistoryHeadCell>
              <S.HistoryHeadCell>Data / Hora</S.HistoryHeadCell>
              <S.HistoryHeadCell>Arquivos</S.HistoryHeadCell>
              <S.HistoryHeadCell>Enviado</S.HistoryHeadCell>
              <S.HistoryHeadCell>Pendente</S.HistoryHeadCell>
              <S.HistoryHeadCell>Erro</S.HistoryHeadCell>
              <S.HistoryHeadCell>Total de Contatos</S.HistoryHeadCell>
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => {
              const situacao = resolveSituacao(item);
              const enviado = item.qtdEnviado ?? 0;
              const pendente = item.qtdPendente ?? 0;
              const erro = item.qtdErro ?? 0;
              const arquivos = item.arquivosUrl?.length || 0;

              return (
                <tr key={item.id}>
                  <S.HistoryCell>
                    <Tooltip
                      title={item.mensagem || "Sem texto"}
                      placement="top-start"
                      arrow
                    >
                      <S.HistoryMessageText as="span">
                        {item.mensagem || "—"}
                      </S.HistoryMessageText>
                    </Tooltip>
                  </S.HistoryCell>
                  <S.HistoryCell>
                    <Tooltip
                      title={SITUACAO_TOOLTIP[situacao]}
                      placement="top"
                      arrow
                    >
                      <S.HistorySituacaoBadge situacao={situacao}>
                        {SITUACAO_LABEL[situacao]}
                      </S.HistorySituacaoBadge>
                    </Tooltip>
                  </S.HistoryCell>
                  <S.HistoryCell>{formatDataHora(item.dataHora)}</S.HistoryCell>
                  <S.HistoryCell>
                    {arquivos > 0 ? (
                      <S.HistoryVerButton
                        type="button"
                        onClick={() => setDetail(item)}
                      >
                        <IoDocumentTextOutline size={14} />
                        {arquivos}
                      </S.HistoryVerButton>
                    ) : (
                      "—"
                    )}
                  </S.HistoryCell>
                  <S.HistoryCell>
                    <Tooltip
                      title={countPillTooltip("enviado", enviado)}
                      placement="top"
                      arrow
                    >
                      <span>
                        <S.HistoryCountPill
                          type="button"
                          tone="enviado"
                          $active={enviado > 0}
                          disabled={enviado === 0}
                          onClick={() =>
                            enviado > 0 &&
                            setLogModal({ entry: item, tone: "enviado" })
                          }
                        >
                          <IoCheckmarkCircleOutline size={14} />
                          {formatQuantidade(enviado)}
                        </S.HistoryCountPill>
                      </span>
                    </Tooltip>
                  </S.HistoryCell>
                  <S.HistoryCell>
                    <Tooltip
                      title={countPillTooltip("pendente", pendente)}
                      placement="top"
                      arrow
                    >
                      <span>
                        <S.HistoryCountPill
                          type="button"
                          tone="pendente"
                          $active={pendente > 0}
                          disabled={pendente === 0}
                          onClick={() =>
                            pendente > 0 &&
                            setLogModal({ entry: item, tone: "pendente" })
                          }
                        >
                          <IoTimeOutline size={14} />
                          {formatQuantidade(pendente)}
                        </S.HistoryCountPill>
                      </span>
                    </Tooltip>
                  </S.HistoryCell>
                  <S.HistoryCell>
                    <Tooltip
                      title={countPillTooltip("erro", erro)}
                      placement="top"
                      arrow
                    >
                      <span>
                        <S.HistoryCountPill
                          type="button"
                          tone="erro"
                          $active={erro > 0}
                          disabled={erro === 0}
                          onClick={() =>
                            erro > 0 &&
                            setLogModal({ entry: item, tone: "erro" })
                          }
                        >
                          <IoAlertCircleOutline size={14} />
                          {formatQuantidade(erro)}
                        </S.HistoryCountPill>
                      </span>
                    </Tooltip>
                  </S.HistoryCell>
                  <S.HistoryCell>
                    <S.HistoryTotalValue>
                      {formatQuantidade(item.qtdTotal)}
                    </S.HistoryTotalValue>
                  </S.HistoryCell>
                </tr>
              );
            })}
          </tbody>
        </S.HistoryTable>

        {!loading && rows.length === 0 && (
          <S.HistoryEmpty>
            Nenhum envio encontrado para o filtro aplicado.
          </S.HistoryEmpty>
        )}
        {loading && <S.HistoryEmpty>Carregando histórico...</S.HistoryEmpty>}
      </S.HistoryTableWrap>

      {detail &&
        createPortal(
          <HistoryDetailModal entry={detail} onClose={() => setDetail(null)} />,
          document.body
        )}

      {logModal &&
        createPortal(
          <HistoryLogModal
            entry={logModal.entry}
            tone={logModal.tone}
            onClose={() => setLogModal(null)}
          />,
          document.body
        )}
    </S.HistoryPanel>
  );
};

interface HistoryDetailModalProps {
  entry: WhatsAppHistoryEntry;
  onClose: () => void;
}

// Nome amigável do arquivo a partir da URL.
function fileNameFromUrl(url: string): string {
  try {
    const clean = url.split("?")[0];
    const parts = clean.split("/");
    const name = decodeURIComponent(parts[parts.length - 1] || "");
    return name || url;
  } catch {
    return url;
  }
}

// Detecta se a URL aponta para uma imagem (visualizável in-page).
function isImageUrl(url: string): boolean {
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url.split("?")[0] || "");
}

function HistoryDetailModal({
  entry,
  onClose,
}: HistoryDetailModalProps): JSX.Element {
  const situacao = resolveSituacao(entry);
  const arquivos = entry.arquivosUrl || [];
  const [imagemAberta, setImagemAberta] = useState<string | null>(null);

  return (
    <S.HistoryDetailBackdrop onClick={onClose}>
      <S.HistoryDetailPanel onClick={(e) => e.stopPropagation()}>
        <S.HistoryLogModalHeader>
          <h4>Detalhes do envio</h4>
          <button type="button" onClick={onClose} aria-label="Fechar">
            <IoClose size={20} />
          </button>
        </S.HistoryLogModalHeader>

        <S.HistoryDetailBody>
          <S.HistoryMetaRow>
            <span>Situação</span>
            <span>{SITUACAO_LABEL[situacao]}</span>
          </S.HistoryMetaRow>
          <S.HistoryMetaRow>
            <span>Data</span>
            <span>{formatDataHora(entry.dataHora)}</span>
          </S.HistoryMetaRow>
          <S.HistoryMetaRow>
            <span>Total de contatos</span>
            <span>{formatQuantidade(entry.qtdTotal)}</span>
          </S.HistoryMetaRow>

          {arquivos.length > 0 && (
            <>
              <S.HistoryDetailSectionTitle style={{ marginTop: 16 }}>
                {arquivos.length} arquivo{arquivos.length > 1 ? "s" : ""}{" "}
                anexado{arquivos.length > 1 ? "s" : ""}
              </S.HistoryDetailSectionTitle>
              <S.HistoryFileChips>
                {arquivos.map((url) => {
                  const imagem = isImageUrl(url);
                  const nome = fileNameFromUrl(url);
                  // Imagens abrem no visualizador in-page; demais arquivos
                  // (PDF, docs, links) abrem em nova aba.
                  return imagem ? (
                    <S.HistoryFileChip
                      key={url}
                      as="button"
                      type="button"
                      onClick={() => setImagemAberta(url)}
                      title={nome}
                    >
                      <IoImageOutline size={14} />
                      <span>{nome}</span>
                    </S.HistoryFileChip>
                  ) : (
                    <S.HistoryFileChip
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      title={nome}
                    >
                      <IoDocumentTextOutline size={14} />
                      <span>{nome}</span>
                    </S.HistoryFileChip>
                  );
                })}
              </S.HistoryFileChips>
            </>
          )}

          <S.HistoryDetailSectionTitle>Mensagem</S.HistoryDetailSectionTitle>
          <S.HistoryDetailErrorBox
            as="div"
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              color: "#334155",
              marginBottom: 0,
            }}
          >
            {entry.mensagem || "Sem texto"}
          </S.HistoryDetailErrorBox>
        </S.HistoryDetailBody>
      </S.HistoryDetailPanel>

      {imagemAberta && (
        <S.HistoryImageLightbox
          onClick={(e) => {
            e.stopPropagation();
            setImagemAberta(null);
          }}
        >
          <S.HistoryImageLightboxClose
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setImagemAberta(null);
            }}
            aria-label="Fechar imagem"
          >
            <IoClose size={20} />
          </S.HistoryImageLightboxClose>
          <img src={imagemAberta} alt="Anexo do envio" />
        </S.HistoryImageLightbox>
      )}
    </S.HistoryDetailBackdrop>
  );
}

interface HistoryLogModalProps {
  entry: WhatsAppHistoryEntry;
  tone: LogTone;
  onClose: () => void;
}

const LOG_TITLE: Record<LogTone, string> = {
  enviado: "Entregues com sucesso",
  pendente: "Pendentes de entrega",
  erro: "Falhas no envio",
};

const LOG_STATUS_LABEL: Record<LogTone, string> = {
  enviado: "Entregue",
  pendente: "Aguardando",
  erro: "Número inválido / falha",
};

// Filtra os destinatários do envio conforme o tom (enviado/pendente/erro).
function recipientsByTone(
  entry: WhatsAppHistoryEntry,
  tone: LogTone
): WhatsAppHistoryRecipient[] {
  const list = entry.destinatarios || [];
  if (tone === "erro") return list.filter((r) => r.status === "ERRO");
  if (tone === "pendente") return list.filter((r) => r.status === "PENDENTE");
  return list.filter(
    (r) =>
      r.status === "ENVIADA" || r.status === "ENTREGUE" || r.status === "LIDA"
  );
}

function HistoryLogModal({
  entry,
  tone,
  onClose,
}: HistoryLogModalProps): JSX.Element {
  const [busca, setBusca] = useState("");

  const Icon =
    // eslint-disable-next-line no-nested-ternary
    tone === "enviado"
      ? IoCheckmarkCircleOutline
      : tone === "pendente"
      ? IoTimeOutline
      : IoAlertCircleOutline;

  const contatos = useMemo(() => recipientsByTone(entry, tone), [entry, tone]);

  const filtrados = useMemo(
    () =>
      contatos.filter(
        (r) =>
          (r.nome || "").toLowerCase().includes(busca.toLowerCase()) ||
          (r.telefone || "").toLowerCase().includes(busca.toLowerCase())
      ),
    [contatos, busca]
  );

  return (
    <S.HistoryDetailBackdrop onClick={onClose}>
      <S.HistoryDetailPanel onClick={(e) => e.stopPropagation()}>
        <S.HistoryLogModalHeader>
          <h4>{LOG_TITLE[tone]}</h4>
          <button type="button" onClick={onClose} aria-label="Fechar">
            <IoClose size={20} />
          </button>
        </S.HistoryLogModalHeader>

        <S.HistoryLogTotal data-tone={tone}>
          Total registrado: <strong>{contatos.length} contatos</strong>
        </S.HistoryLogTotal>

        <S.HistoryLogSubHeader>
          <S.HistoryLogSectionLabel>
            <Icon size={14} />
            Log de contatos
          </S.HistoryLogSectionLabel>
          <S.HistoryLogSearch>
            <IoSearch size={14} color="#94a3b8" />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Pesquisar..."
            />
          </S.HistoryLogSearch>
        </S.HistoryLogSubHeader>

        <S.HistoryLogGrid>
          {filtrados.map((r, idx) => {
            const nome = r.nome || "Sem nome";
            const telefone = r.telefone || "Sem telefone";
            const status = r.erro || LOG_STATUS_LABEL[tone];
            const detalhe = `${telefone} · ${status}`;

            return (
              <S.HistoryLogCard
                // Destinatários não têm id estável e podem repetir nome/telefone
                // (backend às vezes manda null); o índice garante chave única
                // numa lista estática (sem reordenação).
                // eslint-disable-next-line react/no-array-index-key
                key={`${r.telefone}-${r.nome}-${idx}`}
                tone={tone}
              >
                <Icon size={16} />
                <S.HistoryLogCardInfo>
                  {/* Nome e detalhe são truncados com "..." quando longos;
                      o tooltip garante a leitura do texto completo (a
                      mensagem de erro do backend costuma ser extensa). */}
                  <Tooltip title={nome} placement="top" arrow>
                    <strong>{nome}</strong>
                  </Tooltip>
                  <Tooltip
                    title={detalhe}
                    placement="bottom"
                    arrow
                    componentsProps={{
                      tooltip: { sx: { maxWidth: 320 } },
                    }}
                  >
                    <span>{detalhe}</span>
                  </Tooltip>
                </S.HistoryLogCardInfo>
              </S.HistoryLogCard>
            );
          })}

          {filtrados.length === 0 && (
            <S.HistoryEmpty style={{ gridColumn: "1 / -1", textAlign: "left" }}>
              Nenhum contato encontrado.
            </S.HistoryEmpty>
          )}
        </S.HistoryLogGrid>
      </S.HistoryDetailPanel>
    </S.HistoryDetailBackdrop>
  );
}

export default HistoricoEnviosPanel;
